import { useDeferredValue, useEffect, useState, useTransition } from 'react'

import { type InfiniteQueryObserverResult } from 'react-query'
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized'
import styled from 'styled-components'

import { useGetViewPollParticipants } from 'api/post'
import { UserNoOptionsCard } from 'components-ref/atoms'
import { type IUserCard } from 'types/interfaces/IUserCard'
import { type IGetViewPollParticipantsResponse } from 'types/interfaces/post/IGetViewPollParticipantsResponse'

interface IViewPollParticipantsProps {
    pollId: string
    answerId: string
}

export const ViewPollParticipantsUsersList = ({
    pollId,
    answerId
}: IViewPollParticipantsProps) => {
    const [defferedUsers, setDefferedUsers] = useState<IUserCard[]>([])
    const [, startTransition] = useTransition()

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    } = useGetViewPollParticipants(
        pollId,
        answerId
    )

    const defferedData = useDeferredValue(data)

    useEffect(() => {
        if (!isLoading) {
            startTransition(() => {
                if (data) {
                    setDefferedUsers(
                        data.pages.flatMap(page => page.data.results)
                    )
                } else {
                    setDefferedUsers([])
                }
            })
        }
    }, [defferedData, isLoading])

    const users = data ? data.pages.flatMap(page => page.data.results) : []

    const isRowLoaded = ({ index }: { index: number }) => index < users.length

    const loadMoreRows = async (): Promise<
        InfiniteQueryObserverResult<IGetViewPollParticipantsResponse>
    > => {
        if (!hasNextPage || isFetchingNextPage) {
            // eslint-disable-next-line @typescript-eslint/return-await
            return new Promise((_resolve, reject) => reject)
        }

        return await fetchNextPage()
    }

    return (
        <StyledContainer>
            <InfiniteLoader
                isRowLoaded={isRowLoaded}
                loadMoreRows={loadMoreRows}
                rowCount={defferedUsers.length + (hasNextPage ? 1 : 0)}
            >
                {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                        {({ width, height }) => (
                            <List
                                ref={list => registerChild(list)}
                                onRowsRendered={onRowsRendered}
                                width={width}
                                height={height}
                                rowCount={defferedUsers.length}
                                rowHeight={70}
                                rowRenderer={({ index, key, style }) => (
                                    <UserNoOptionsCard
                                        item={defferedUsers[index]}
                                        key={key}
                                        style={style}
                                    />
                                )}
                            />
                        )}
                    </AutoSizer>
                )}
            </InfiniteLoader>
        </StyledContainer>
    )
}

const StyledContainer = styled.div`
    height: 30rem;
    max-height: 30rem;
    & > * + * {
        margin-top: 10px;
    }
`
